import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { environment } from '@/config/environment';
import { loadGoogleMaps } from '@/features/maps/googleMaps';
import {
  getAllTechnicianStops,
  saveRouteWeighting,
  type RouteStop,
} from '@/features/scheduling/api/scheduling';

const today = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};
const addressLabel = (stop: RouteStop) =>
  [
    stop.service_location.address?.street_number,
    stop.service_location.address?.route,
    stop.service_location.address?.locality,
    stop.service_location.address?.postal_code,
  ]
    .filter(Boolean)
    .join(', ');
export function TechnicianRoutePage() {
  const navigate = useNavigate();
  const { technicianId: rawId } = useParams();
  const [params] = useSearchParams();
  const technicianId = Number(rawId);
  const date = params.get('date') ?? today();
  const query = useQuery({
    queryKey: ['mission-control', date],
    queryFn: () => getAllTechnicianStops(date),
    enabled: Number.isInteger(technicianId) && technicianId > 0,
  });
  const day = query.data?.find((candidate) => candidate.technician.id === technicianId);
  if (!Number.isInteger(technicianId) || technicianId <= 0 || query.isError)
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        This technician route could not be loaded.
      </Alert>
    );
  if (query.isLoading)
    return (
      <Box sx={{ p: 4, display: 'grid', placeItems: 'center' }}>
        <CircularProgress aria-label="Loading technician route" />
      </Box>
    );
  if (!day)
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        This technician has no route for the selected day.
      </Alert>
    );
  return (
    <RouteEditor
      key={`${technicianId}-${date}`}
      technicianId={technicianId}
      technicianName={day.technician.name}
      date={date}
      initialStops={day.route_stops}
      onBack={() => void navigate('/foundation')}
    />
  );
}
function RouteEditor({
  technicianId,
  technicianName,
  date,
  initialStops,
  onBack,
}: {
  technicianId: number;
  technicianName: string;
  date: string;
  initialStops: RouteStop[];
  onBack: () => void;
}) {
  const [stops, setStops] = useState(initialStops);
  const [dragged, setDragged] = useState<number | null>(null);
  const save = useMutation({
    mutationFn: (ordered: RouteStop[]) =>
      Promise.all(
        ordered.map((stop, index) => saveRouteWeighting(stop.id, date, technicianId, index)),
      ),
  });
  const reorder = (from: number, to: number) => {
    if (from === to) return;
    const next = [...stops];
    const [item] = next.splice(from, 1);
    if (!item) return;
    next.splice(to, 0, item);
    setStops(next);
    save.mutate(next);
  };
  const completed = stops.filter((stop) => stop.service_report_completed).length;
  return (
    <Box sx={{ p: 1 }}>
      <Paper elevation={0} sx={{ ...paperSx, mb: 1.5, px: 1, py: 0.5 }}>
        <Button sx={commandButtonSx} startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back to mission control
        </Button>
      </Paper>
      <Paper
        elevation={0}
        sx={{
          ...paperSx,
          mb: 1.5,
          px: 2,
          py: 1.75,
          borderTop: '3px solid',
          borderTopColor: 'primary.main',
        }}
      >
        <Typography color="primary.main">
          {new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </Typography>
      </Paper>
      <Paper elevation={0} sx={{ ...paperSx, mb: 1.5, p: 2 }}>
        <Stack direction="row" gap={2} alignItems="center">
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            {technicianName
              .split(/\s+/)
              .map((part) => part[0])
              .join('')
              .slice(0, 2)}
          </Avatar>
          <Box>
            <Typography variant="h1">{technicianName}</Typography>
            <Typography color="text.secondary">
              {completed}/{stops.length} stops complete
            </Typography>
          </Box>
        </Stack>
      </Paper>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(420px,.9fr) minmax(440px,1.1fr)' },
          gap: 1.5,
        }}
      >
        <Paper elevation={0} sx={paperSx}>
          <Typography variant="h2" sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            Route
          </Typography>
          {stops.map((stop, index) => (
            <Stack
              key={stop.id}
              draggable={!stop.cancellation}
              onDragStart={() => setDragged(index)}
              onDragEnd={() => setDragged(null)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => {
                if (dragged !== null) reorder(dragged, index);
                setDragged(null);
              }}
              direction="row"
              gap={1.25}
              alignItems="center"
              sx={{
                p: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: dragged === index ? '#f3f2f1' : 'white',
                cursor: stop.cancellation ? 'default' : 'grab',
              }}
            >
              <DragIndicatorIcon color="disabled" />
              <Chip
                label={String.fromCharCode(65 + index)}
                color={stop.cancellation ? 'error' : 'primary'}
              />
              <Box sx={{ flex: 1 }}>
                <Button
                  component={Link}
                  to={`/clients/${stop.service_location.client.id}`}
                  sx={{ p: 0, color: 'text.primary', fontWeight: 600, textTransform: 'none' }}
                >
                  {stop.service_location.client.name}
                </Button>
                <Typography variant="body2">
                  {addressLabel(stop) || stop.service_location.name}
                </Typography>
                {Boolean(stop.cancellation) && (
                  <Typography variant="caption" color="error.main">
                    Cancelled
                  </Typography>
                )}
                {stop.service_report_completed && stop.service_report_id && (
                  <Button
                    component={Link}
                    to={`/clients/${stop.service_location.client.id}/service-locations/${stop.service_location.id}/reports/${stop.service_report_id}`}
                    size="small"
                    sx={{ mt: 0.5, px: 0, minWidth: 0, textTransform: 'none' }}
                  >
                    View completed report
                  </Button>
                )}
              </Box>
            </Stack>
          ))}
          {save.isPending && (
            <Typography color="text.secondary" sx={{ p: 1.5 }}>
              Saving route order…
            </Typography>
          )}
          {save.isError && <Alert severity="error">The new route order could not be saved.</Alert>}
        </Paper>
        <RouteMap stops={stops} />
      </Box>
    </Box>
  );
}
function RouteMap({ stops }: { stops: RouteStop[] }) {
  const destinations = stops
    .filter((stop) => !stop.cancellation)
    .map((stop) => addressLabel(stop) || stop.service_location.name);
  const mapElement = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!environment.googleMapsApiKey || destinations.length === 0 || !mapElement.current) return;
    let renderer: google.maps.DirectionsRenderer | undefined;
    let active = true;
    void loadGoogleMaps(environment.googleMapsApiKey)
      .then((google) => {
        if (!active || !mapElement.current) return;
        const map = new google.maps.Map(mapElement.current, {
          center: { lat: 51.5459, lng: 0.7108 },
          zoom: 12,
          mapTypeControl: true,
          streetViewControl: true,
        });
        renderer = new google.maps.DirectionsRenderer({ map });
        const service = new google.maps.DirectionsService();
        void service.route(
          {
            origin: destinations[0] ?? '',
            destination: destinations.at(-1) ?? '',
            waypoints: destinations.slice(1, -1).map((location) => ({ location, stopover: true })),
            travelMode: google.maps.TravelMode.DRIVING,
          },
          (result, status) => {
            if (!active) return;
            if (status === 'OK' && result) renderer?.setDirections(result);
            else setError('Google Maps could not calculate this route. Check the saved addresses.');
          },
        );
      })
      .catch(() => {
        if (active) setError('Google Maps could not be loaded for this environment.');
      });
    return () => {
      active = false;
      renderer?.setMap(null);
    };
  }, [destinations]);
  return (
    <Paper elevation={0} sx={{ ...paperSx, overflow: 'hidden', minHeight: 500 }}>
      <Typography variant="h2" sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        Route map
      </Typography>
      {environment.googleMapsApiKey && destinations.length ? (
        <>
          <Box
            ref={mapElement}
            role="img"
            aria-label="Ordered technician route map"
            sx={{ width: '100%', height: 460 }}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </>
      ) : (
        <Alert severity="info" sx={{ m: 2 }}>
          {environment.googleMapsApiKey
            ? 'There are no active stops to display.'
            : 'Google Maps has not been configured for this environment.'}
        </Alert>
      )}
    </Paper>
  );
}

const paperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: '0 2px 6px rgba(0,0,0,.12)',
};
const commandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'primary.main' },
};
